// ============================================================
// FILE: src/pages/Products.jsx  →  PRODUCTS CATALOGUE PAGE
// The product grid with search bar + category filter buttons.
// Product data (names, images, specs) comes from the database —
// edit it in backend/seed_data.py (PRODUCTS CATALOGUE).
// ============================================================

import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Search, PackageOpen, Star } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../hooks/useSEO";
import { api } from "../lib/api";
import { PRODUCT_CATEGORIES } from "../data/site";

// ===== FEATURED PRODUCTS (SHOWN FIRST WITH GOLD BADGE) =====
// These products always appear at the TOP of the grid with a
// gold "Featured" badge. Add any product "slug" from
// backend/seed_data.py to feature it.
const FEATURED_SLUGS = ["basmati-rice", "thermal-coal"];

export default function Products() {
  useSEO("Products — Evolve Trade & Logistics", "Export-ready agriculture, spices, fresh produce, dry fruits, chemicals, construction materials, industrial products, packaging, FMCG, textiles and metals.");
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const active = params.get("category") || "All";

  useEffect(() => {
    api.products().then((p) => setProducts(p)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return products
      .filter((p) => {
        const matchCat = active === "All" || p.category === active;
        const matchQ = !query || p.name.toLowerCase().includes(query.toLowerCase()) || p.category.toLowerCase().includes(query.toLowerCase());
        return matchCat && matchQ;
      })
      .sort((a, b) => {
        const fa = FEATURED_SLUGS.indexOf(a.slug);
        const fb = FEATURED_SLUGS.indexOf(b.slug);
        return (fa === -1 ? 99 : fa) - (fb === -1 ? 99 : fb);
      });
  }, [products, active, query]);

  const setCategory = (c) => {
    if (c === "All") params.delete("category");
    else params.set("category", c);
    setParams(params, { replace: true });
  };

  return (
    <>
      <PageHero
        label="Product Catalogue"
        title="Sourced at origin,"
        accent="shipped with certainty"
        description="Eleven categories of export-ready commodities and products — each with verified suppliers, inspected quality and destination-ready documentation."
        crumbs={[{ label: "Products" }]}
      />

      <section className="pb-24 sm:pb-32" data-testid="products-catalog">
        <div className="container-x">
          <div className="glass sticky top-[84px] z-40 mb-10 rounded-2xl p-4" data-testid="products-filter-bar">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
              <div className="relative lg:w-72">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label="Search products"
                  placeholder="Search products…"
                  className="field !pl-11"
                  data-testid="products-search-input"
                />
              </div>
              {/* Category filter pills — scroll sideways on phone, wrap on larger screens */}
              <div className="flex flex-nowrap gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex-wrap sm:overflow-visible sm:pb-0">
                {["All", ...PRODUCT_CATEGORIES].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold transition-[background-color,color,border-color] duration-300 ${
                      active === c
                        ? "bg-gradient-to-r from-blue-700 to-sky-500 text-white shadow-lg shadow-blue-900/30"
                        : "border border-slate-900/10 text-muted-foreground hover:border-teal-500/50 hover:text-teal-500 dark:border-white/10"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] animate-pulse rounded-2xl bg-slate-900/5 dark:bg-white/5" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center py-24 text-center" data-testid="products-empty">
              <PackageOpen className="h-12 w-12 text-muted-foreground" />
              <p className="mt-4 font-display text-xl font-semibold">No products found</p>
              <p className="mt-2 text-sm text-muted-foreground">Try a different search or category — or ask us to source it for you.</p>
              <Link to="/contact" className="btn-secondary mt-6" data-testid="products-empty-cta">Request Sourcing</Link>
            </div>
          ) : (
            <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <AnimatePresence mode="popLayout">
                {filtered.map((p) => (
                  <motion.div
                    key={p.slug}
                    layout
                    initial={{ opacity: 0, scale: 0.92 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.92 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link to={`/products/${p.slug}`} className="group block" data-testid={`product-card-${p.slug}`}>
                      <div className="relative overflow-hidden rounded-2xl border border-slate-900/10 transition-[border-color,box-shadow] duration-500 group-hover:border-teal-500/50 group-hover:shadow-[0_24px_70px_-24px_rgba(20,184,166,0.35)] dark:border-white/10">
                        <div className="aspect-[4/3] overflow-hidden">
                          <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/10 to-transparent" />
                        <span className="glass absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-teal-300">{p.category}</span>
                        {FEATURED_SLUGS.includes(p.slug) && (
                          <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-950" data-testid={`featured-badge-${p.slug}`}>
                            <Star className="h-3 w-3 fill-slate-950" /> Featured
                          </span>
                        )}
                        <div className="absolute inset-x-0 bottom-0 p-5">
                          <h3 className="font-display text-lg font-semibold text-white">{p.name}</h3>
                          <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-300">
                            MOQ: {p.moq}
                            <ArrowRight className="h-3 w-3 text-teal-400 opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                          </p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>
    </>
  );
}
